import { Link } from "react-router-dom";
import { FileText, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#1A1A2E] text-white py-12">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/" className="inline-flex items-center text-white/70 hover:text-white mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-8 h-8 text-blue-400" />
            <h1 className="text-3xl font-bold">Terms of Service</h1>
          </div>
          <p className="text-white/70">Last updated: December 2024</p>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-xl border border-gray-200 p-8 md:p-12 prose prose-slate max-w-none">
          
          <h2>1. Acceptance of Terms</h2>
          <p>
            By accessing or using 1031Condo.com (the "Website"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the Website. We reserve the right to modify these Terms at any time, and your continued use constitutes acceptance of any changes.
          </p>

          <h2>2. Description of Services</h2>
          <p>
            1031Condo.com provides an online website for viewing luxury condo unit properties that may be suitable for 1031 exchange transactions. Our services include:
          </p>
          <ul>
            <li>Property listing information and search functionality</li>
            <li>Educational resources about 1031 exchanges</li>
            <li>Tax savings calculators and estimation tools</li>
            <li>Contact facilitation with real estate professionals</li>
          </ul>

          <h2>3. User Accounts</h2>
          <p>
            To access certain features, you may need to create an account. You agree to:
          </p>
          <ul>
            <li>Provide accurate and complete information</li>
            <li>Maintain the security of your account credentials</li>
            <li>Notify us immediately of any unauthorized access</li>
            <li>Be responsible for all activities under your account</li>
          </ul>

          <h2>4. Intellectual Property</h2>
          <p>
            All content on this Website, including text, graphics, logos, images, and software, is the property of 1031Condo.com or its licensors and is protected by copyright, trademark, and other intellectual property laws. You may not reproduce, distribute, modify, or create derivative works without our express written permission.
          </p>

          <h2>5. User Conduct</h2>
          <p>You agree not to:</p>
          <ul>
            <li>Use the Website for any unlawful purpose</li>
            <li>Interfere with or disrupt the Website's operation</li>
            <li>Attempt to gain unauthorized access to any systems</li>
            <li>Transmit viruses, malware, or harmful code</li>
            <li>Scrape, data mine, or collect information without permission</li>
            <li>Impersonate any person or entity</li>
            <li>Use the Website to send spam or unsolicited communications</li>
          </ul>

          <h2>6. Third-Party Links</h2>
          <p>
            The Website may contain links to third-party websites. We are not responsible for the content, accuracy, or practices of these external sites. Accessing third-party links is at your own risk.
          </p>

          <h2>7. Disclaimer of Warranties</h2>
          <p>
            THE WEBSITE AND ALL CONTENT ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. WE DISCLAIM ALL WARRANTIES, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
          </p>
          <p>
            We do not warrant that:
          </p>
          <ul>
            <li>The Website will be uninterrupted or error-free</li>
            <li>Property information is accurate, complete, or current</li>
            <li>Results from using the Website will meet your expectations</li>
          </ul>

          <h2>8. Limitation of Liability</h2>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, 1031CONDO.COM, ITS OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR OTHER INTANGIBLE LOSSES, ARISING FROM:
          </p>
          <ul>
            <li>Your use or inability to use the Website</li>
            <li>Any errors or omissions in content</li>
            <li>Unauthorized access to your information</li>
            <li>Any third-party conduct on the Website</li>
          </ul>

          <h2>9. Indemnification</h2>
          <p>
            You agree to indemnify and hold harmless 1031Condo.com and its affiliates from any claims, damages, losses, or expenses (including attorneys' fees) arising from your use of the Website, violation of these Terms, or infringement of any rights of another party.
          </p>

          <h2>10. Governing Law and Jurisdiction</h2>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of the State of New York, without regard to conflict of law principles. Any disputes shall be resolved in the state or federal courts located in New York County, New York.
          </p>

          <h2>11. Arbitration Agreement</h2>
          <p>
            Any controversy or claim arising out of or relating to these Terms shall be settled by binding arbitration in accordance with the rules of the American Arbitration Association. The arbitration shall take place in New York, New York.
          </p>

          <h2>12. Severability</h2>
          <p>
            If any provision of these Terms is found to be unenforceable, the remaining provisions shall continue in full force and effect.
          </p>

          <h2>13. Entire Agreement</h2>
          <p>
            These Terms, together with our Privacy Policy and Disclaimer, constitute the entire agreement between you and 1031Condo.com regarding your use of the Website.
          </p>

          <h2>14. Contact Information</h2>
          <p>For questions about these Terms, contact us:</p>
          <ul>
            <li>Contact: <a href="/contact">Send us a message</a></li>
            <li>Company: Krieger Real Estate LLC</li>
            <li>Website: 1031Condo.com</li>
          </ul>

          <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-sm text-gray-700 mb-0">
              <strong>Company Information:</strong> 1031Condo.com is owned and operated by Krieger Real Estate LLC, a licensed real estate broker in New York State (License #10491202190) and Florida (License #CQ1072150).
            </p>
          </div>
        </div>

        <div className="mt-8 flex gap-4">
          <Link to="/privacy">
            <Button variant="outline">Privacy Policy</Button>
          </Link>
          <Link to="/disclaimer">
            <Button variant="outline">Disclaimer</Button>
          </Link>
        </div>
      </main>
    </div>
  );
}
